// La sección Instagram de la sidebar — ADR-2026-10-06-instagram-tiene-su-propia-seccion.
//
// Entra directo al calendario de la marca: sin listado ni "Nuevo lanzamiento" de por
// medio. Por dentro sigue siendo un lanzamiento `redes`
// (ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts), pero eso ya no
// se ve: hay uno por marca y se crea solo la primera vez.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Instagram, Loader2 } from 'lucide-react';
import { useBrandProfile } from '../../../hooks/useBrandProfile';
import { usePreLanzamientos } from '../../../hooks/useFuncionesAvanzadas';
import { reglasDe } from '../canales';
import { CalendarioDeRedes } from './CalendarioDeRedes';
import { calendarioDeLaMarca, type CalendarioDeLaMarca } from './postsApi';

export default function CalendarioDeInstagram() {
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();
  const habilitado = usePreLanzamientos();
  const companyId = profile?.id_company ?? null;

  const [calendario, setCalendario] = useState<CalendarioDeLaMarca | null>(null);
  const [cargando, setCargando] = useState(true);
  /** Con un post abierto el encabezado es el del editor ("Nuevo post"): éste hablaría del mes. */
  const [editando, setEditando] = useState(false);

  /** El último pedido: si se cambia de marca con uno en vuelo, el de la marca vieja no pisa. */
  const pedidoRef = useRef(0);
  const cargar = useCallback(async () => {
    if (!companyId || !habilitado) return;
    const n = ++pedidoRef.current;
    const c = await calendarioDeLaMarca(companyId, currentBrandId);
    if (n !== pedidoRef.current) return;
    setCalendario(c);
    setCargando(false);
  }, [companyId, currentBrandId, habilitado]);

  // Al cambiar de marca se vuelve a "cargando": si no, se ve un instante el calendario
  // de la marca anterior.
  useEffect(() => {
    setCargando(true);
    void cargar();
  }, [cargar]);

  if (!habilitado) {
    return (
      <div className="mx-auto max-w-2xl py-16 px-6 text-center">
        <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
        <h1 className="mt-4 text-lg font-semibold text-brand-black">
          El calendario de Instagram está desactivado
        </h1>
        <p className="mt-2 text-sm text-neutral-500">
          Se activa desde Configuración, en Funciones avanzadas.
        </p>
        <button
          type="button"
          onClick={() => navigate('/configuracion', { state: { section: 'funcionesAvanzadas' } })}
          className="mt-5 rounded-xl bg-brand-black py-2.5 px-5 text-sm font-medium text-white"
        >
          Ir a Configuración
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-4 px-4 md:py-8 md:px-8">
      {/* El `<h1>` es hijo DIRECTO de la fila, hermano del ícono: el aire de arriba del
          shell se lo lleva la fila entera (ADR-2026-09-15-el-titulo-no-se-puede-envolver). */}
      {!editando && (
        <>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white shadow-sm">
              <Instagram className="w-5 h-5" />
            </span>
            <h1 className="text-xl font-semibold text-brand-black md:text-2xl">Instagram</h1>
          </div>
          <p className="mt-1 hidden pl-[52px] text-sm text-neutral-500 md:block">
            {calendario && !cargando
              ? `${calendario.posts} post${calendario.posts === 1 ? '' : 's'} planificado${
                  calendario.posts === 1 ? '' : 's'
                } · qué sube, con qué texto y a qué hora`
              : 'Qué sube, con qué texto y a qué hora'}
          </p>
        </>
      )}

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : !calendario || !companyId ? (
        <div className="mx-auto max-w-lg py-24 px-6 text-center">
          <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
          <p className="mt-4 text-sm text-brand-black">No pudimos abrir tu calendario</p>
          <button
            type="button"
            onClick={() => {
              setCargando(true);
              void cargar();
            }}
            className="mt-4 text-sm text-neutral-500 underline"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <CalendarioDeRedes
          lanzamientoId={calendario.id}
          companyId={companyId}
          fechaLanzamiento={calendario.fechaLanzamiento}
          aceptaVideo={reglasDe('redes').conVideo}
          onCambio={() => void cargar()}
          onEditando={setEditando}
        />
      )}
    </div>
  );
}
